import {
    Grid,
    MenuItem,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { ToolbarButton } from '@tassat/button';
import { AdminRoleId } from '@tassat/common-definitions';
import { HeaderLogos } from '@tassat/layout';
import React from 'react';

import authService from 'Lib/auth/AuthService';
import { NON_ALPHANUMERIC_REGEX } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole, getUser } from 'Selectors/common';
import { useClientLogos } from 'SharedComponents/hooks/useClientLogos';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import ClientUserSearch from 'SharedComponents/users/ClientUserSearch';
import UserMenu from 'SharedComponents/users/userMenu';

import { muiStyles } from './styles';

import { HeaderProps } from '.';

export default function GlobalHeader(props: HeaderProps) {
    const { location } = props;

    const userRole = useAppSelector(getCurrentRole);
    const user = useAppSelector(getUser);
    const applicationTheme = useTheme<ApplicationMuiTheme>();
    const classes = muiStyles(props);
    const [features] = useFeatures();
    const [ userMenuAnchor, setUserMenuAnchorEl ] = React.useState<null | HTMLElement>(null);
    const handleUserMenuClose = () => setUserMenuAnchorEl(null);

    const clientLogoUrls = useClientLogos();

    const showLogoutButton: boolean = React.useMemo(() => !(features.isSSOEnabled &&
        (location.pathname === '/invalid' || location.pathname === '/pendingSetup' || location.pathname === '/requestCreated')),
    [ features.isSSOEnabled, location.pathname ]);

    const userMenuItems = React.useMemo(() => {
        const menuItems = [];

        if (user?.IsAdmin && applicationTheme?.app.tassat.legalFiles.length > 0) {
            menuItems.push(applicationTheme.app.tassat.legalFiles.map((file: any, i) => {
                return (
                    <a
                        className={classes.menuLink}
                        href={file.url}
                        key={file.url}
                        rel='noreferrer'
                        target='_blank'
                    >
                        <MenuItem
                            id={`sidebar_user_menu_${file.fileName.replace(NON_ALPHANUMERIC_REGEX, '')}`}
                            key={i + '_' + file.fileName}
                        >
                            {file.fileDescription}
                        </MenuItem>
                    </a>
                );
            }));
        }

        return menuItems;
    }, [
        user?.IsAdmin,
        applicationTheme.app.tassat.legalFiles,
        classes.menuLink
    ]);

    return (
        <Grid
            alignItems='center'
            className={classes.globalHeaderContainer}
            container
            direction='row'
            justifyContent='space-between'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                alignItems='center'
                className={classes.iconsContainer}
                container
                direction='row'
                justifyContent='flex-start'
                spacing={0}
                wrap='nowrap'
            >
                <HeaderLogos
                    clientLogoUrl={clientLogoUrls.ApplicationLogoUrl}
                    logoHeight={65}
                />
            </Grid>
            <Grid
                alignItems='center'
                container
                direction='row'
                justifyContent='flex-end'
                spacing={0}
                wrap='nowrap'
            >
                {user?.IsAdmin &&
                    [
                    AdminRoleId.BANK_OPS as string,
                    AdminRoleId.BANK_OPS_SUPERVISOR as string,
                    AdminRoleId.CLIENT_SERVICES as string
                ].includes(userRole?.AdminRoleId || '') && (
                    <ClientUserSearch />
                )}
                {user && (
                    <>
                        <Typography
                            className={`${classes.nameLabel} ${userMenuItems.length ? classes.clickableName : ''}`}
                            onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}
                            variant='body1'
                        >
                            Welcome, {user.FirstName}
                        </Typography>
                        <UserMenu
                            handleMenuClose={handleUserMenuClose}
                            menuAnchor={userMenuAnchor}
                            menuItems={userMenuItems}
                        />
                    </>
                )}
                {showLogoutButton && (
                    <ToolbarButton
                        className={classes.logout}
                        onClick={() => authService.logout()}
                        text='LOG OUT'
                    />
                )}
            </Grid>
        </Grid>
    );
}